import { makeStyles } from '@material-ui/core/styles';
import { redesignTheme } from '../../../theme';
import { minorTestShellStyles } from '../shell.styles';

const { colors, fonts, fontSize, spacing, radius, breakpoints, sizes } = redesignTheme;

export const useMinorTestPassStyles = makeStyles(() => ({
  ...minorTestShellStyles,
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minHeight: 56,
    padding: `12px ${spacing.md}px`,
    backgroundColor: colors.white,
    borderBottom: `1px solid ${colors.surfaceBorder}`,
    boxSizing: 'border-box',
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      flexWrap: 'wrap',
    },
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      padding: `12px ${spacing.sm}px`,
      gap: spacing.xs,
    },
  },
  progressWrap: {
    flex: '1 1 360px',
    maxWidth: 584,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      order: 3,
      flexBasis: '100%',
      maxWidth: 'none',
    },
  },
  progressTrack: {
    flex: 1,
    height: 8,
    borderRadius: radius.sm,
    backgroundColor: colors.progressTrack,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: radius.sm,
    backgroundColor: colors.primary,
    transition: 'width 180ms ease',
  },
  progressText: {
    minWidth: 34,
    margin: 0,
    color: colors.primary,
    fontFamily: fonts.gothic,
    fontSize: fontSize.md,
    letterSpacing: 1.26,
    textAlign: 'right',
  },
  main: {
    padding: '24px 56px 48px',
    boxSizing: 'border-box',
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      padding: `${spacing.md}px ${spacing.sm}px ${spacing.xl}px`,
    },
  },
  topActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
    maxWidth: 1280,
    margin: '0 auto 16px',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  contentCard: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: 40,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    boxSizing: 'border-box',
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      padding: spacing.md,
    },
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      padding: spacing.sm,
    },
  },
  questionLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 580px',
    gap: 40,
    alignItems: 'start',
    [`@media (max-width: ${breakpoints.tablet}px)`]: {
      gridTemplateColumns: '1fr',
      gap: spacing.lg,
    },
  },
  questionTitle: {
    margin: '0 0 20px',
    color: colors.primary,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.xl,
    lineHeight: 1.25,
  },
  questionText: {
    color: colors.primaryDark,
    fontFamily: fonts.opensans,
    fontSize: fontSize.base,
    lineHeight: 1.5,
    '& p, & h3': {
      margin: 0,
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
  questionMedia: {
    marginTop: spacing.md,
  },
  mediaImage: {
    display: 'block',
    width: '100%',
    maxHeight: 360,
    objectFit: 'contain',
    borderRadius: radius.sm,
  },
  responses: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
  },
  responseButton: {
    width: '100%',
    minHeight: 52,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: spacing.sm,
    border: '1px solid #d9d9d9',
    borderRadius: radius.sm,
    backgroundColor: colors.white,
    color: colors.primaryDark,
    cursor: 'pointer',
    boxSizing: 'border-box',
    textAlign: 'left',
    fontFamily: fonts.opensans,
    '&:hover': {
      borderColor: colors.primary,
    },
    '&.selected': {
      borderColor: colors.primary,
      backgroundColor: 'rgba(0, 10, 141, 0.04)',
    },
  },
  radio: {
    flex: '0 0 auto',
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '1px solid #d9d9d9',
    backgroundColor: colors.white,
    boxSizing: 'border-box',
    position: 'relative',
    '.selected &': {
      borderColor: colors.primary,
    },
    '.selected &::after': {
      content: '""',
      position: 'absolute',
      inset: 3,
      borderRadius: '50%',
      backgroundColor: colors.primary,
    },
  },
  responseText: {
    flex: 1,
    minWidth: 0,
    fontSize: fontSize.md,
    lineHeight: 1.4,
    letterSpacing: 0.28,
    '& p, & h3': {
      margin: 0,
    },
  },
  imageGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: spacing.sm,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      gridTemplateColumns: '1fr',
    },
  },
  imageOption: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    padding: 0,
    border: '2px solid transparent',
    borderRadius: radius.sm,
    backgroundColor: colors.progressTrackMuted,
    cursor: 'pointer',
    overflow: 'hidden',
    '&:hover': {
      borderColor: colors.primary,
    },
    '&.selected': {
      borderColor: colors.primary,
    },
  },
  imageOptionImg: {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'cover',
  },
  imageRadio: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `1px solid ${colors.white}`,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    '.selected &': {
      borderColor: colors.primary,
      backgroundColor: colors.white,
    },
    '.selected &::after': {
      content: '""',
      position: 'absolute',
      inset: 3,
      borderRadius: '50%',
      backgroundColor: colors.primary,
    },
  },
  footerNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginTop: 40,
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
    },
  },
  primaryButton: {
    borderColor: colors.primaryDark,
    backgroundColor: colors.primaryDark,
    color: colors.white,
    '&:hover': {
      backgroundColor: colors.primaryDark,
      opacity: 0.92,
    },
    '&:disabled': {
      backgroundColor: colors.primaryDark,
      color: colors.white,
    },
  },
  timerIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
    display: 'block',
    color: colors.primaryDark,
  },
  timerChip: {
    minHeight: 40,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '0 24px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    backgroundColor: colors.white,
    color: colors.primaryDark,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    lineHeight: 1,
    letterSpacing: 0.28,
  },
  timerValue: {
    display: 'block',
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    lineHeight: 1,
    letterSpacing: 0.28,
    color: colors.primaryDark,
    fontVariantNumeric: 'tabular-nums',
  },
  loadingCenter: {
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
  },
  loadingText: {
    margin: 0,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.base,
    color: colors.primaryDark,
    textAlign: 'center',
  },
  unavailable: {
    maxWidth: 720,
    margin: '96px auto',
    padding: spacing.lg,
    borderRadius: radius.md,
    backgroundColor: colors.white,
    textAlign: 'center',
  },
  unavailableTitle: {
    margin: '0 0 24px',
    fontFamily: fonts.gothic,
    fontSize: fontSize.xl,
    color: colors.primaryDark,
  },
  emailCaptureCard: {
    maxWidth: 900,
    margin: '0 auto',
    padding: spacing.lg,
    borderRadius: radius.md,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
  },
  emailLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 40%) 1fr',
    gap: spacing.lg,
    alignItems: 'center',
    [`@media (max-width: ${breakpoints.mobile}px)`]: {
      gridTemplateColumns: '1fr',
    },
  },
  emailImage: {
    width: '100%',
    borderRadius: radius.sm,
    display: 'block',
  },
  emailTitle: {
    margin: '0 0 8px',
    fontFamily: fonts.gothicBold,
    fontSize: fontSize.xl,
    color: colors.primary,
  },
  emailDescription: {
    margin: '0 0 20px',
    fontSize: fontSize.md,
    lineHeight: 1.5,
    color: colors.primaryDark,
  },
  emailLabel: {
    margin: '0 0 6px',
    fontSize: fontSize.md,
    fontFamily: fonts.opensansSemiBold,
  },
  emailInput: {
    fontFamily: fonts.opensans,
    fontSize: fontSize.md,
    borderRadius: radius.sm,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.border,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.primary,
      borderWidth: 1,
    },
  },
  emailButton: {
    width: '100%',
    marginTop: spacing.md,
    minHeight: 40,
    borderRadius: radius.sm,
    backgroundColor: colors.primaryDark,
    color: colors.white,
    fontFamily: fonts.opensansSemiBold,
    fontSize: fontSize.md,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: colors.primaryDark,
      opacity: 0.92,
    },
  },
}));
